// PainterOps export utilities
// CSV + PDF generators for Customers, Invoices, Stops.
//
// jspdf is loaded lazily because its bundle pulls in optional peer deps
// (html2canvas, canvg) that Vite's pre-bundler chokes on. The app only
// needs jspdf when the user actually exports a PDF.
let _jsPDF;
async function loadJsPDF() {
  if (_jsPDF) return _jsPDF;
  const mod = await import('jspdf');
  _jsPDF = mod.jsPDF;
  return _jsPDF;
}

// ─── CSV ─────────────────────────────────────────────────────
function csvEscape(v) {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function toCsv(rows, headers) {
  const headerLine = headers.map(h => csvEscape(h.label)).join(',');
  const lines = rows.map(r =>
    headers.map(h => csvEscape(typeof h.value === 'function' ? h.value(r) : r[h.value])).join(',')
  );
  return [headerLine, ...lines].join('\n');
}

function downloadFile(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 0);
}

// ─── Schemas ─────────────────────────────────────────────────
const CUSTOMER_FIELDS = [
  { label: 'Name',        value: 'name' },
  { label: 'Address',     value: 'addr' },
  { label: 'Phone',       value: 'phone' },
  { label: 'Email',       value: 'email' },
  { label: 'Tech',        value: 'tech' },
  { label: 'MRR ($)',     value: 'mrr' },
  { label: 'Health',      value: 'health' },
  { label: 'Pool (gal)',  value: 'poolGal' },
  { label: 'Status',      value: 'status' },
  { label: 'Autopay',     value: c => c.autopay ? 'Yes' : 'No' },
  { label: 'Controller',  value: 'controller' },
  { label: 'Since',       value: 'since' },
  { label: 'Gate Code',   value: 'gateCode' },
  { label: 'Notes',       value: 'notes' },
];

const INVOICE_FIELDS = [
  { label: 'Invoice #',   value: 'id' },
  { label: 'Customer',    value: i => i.customerName || `#${i.custId}` },
  { label: 'Service',     value: 'service' },
  { label: 'Amount ($)',  value: 'amount' },
  { label: 'Date',        value: 'date' },
  { label: 'Due',         value: 'due' },
  { label: 'Status',      value: 'status' },
  { label: 'Paid Date',   value: i => i.paidDate || '' },
];

const STOP_FIELDS = [
  { label: 'Stop #',      value: 'order' },
  { label: 'Customer',    value: s => s.customerName || `#${s.custId}` },
  { label: 'Address',     value: 'customerAddr' },
  { label: 'Tech',        value: 'tech' },
  { label: 'Date',        value: 'date' },
  { label: 'Time',        value: 'time' },
  { label: 'Status',      value: 'status' },
  { label: 'Duration',    value: s => s.duration ? `${s.duration}m` : '' },
  { label: 'Cl',          value: s => s.chem?.cl ?? '' },
  { label: 'pH',          value: s => s.chem?.ph ?? '' },
  { label: 'Alk',         value: s => s.chem?.alk ?? '' },
  { label: 'CYA',         value: s => s.chem?.cya ?? '' },
  { label: 'Notes',       value: 'notes' },
];

// Enrich stops with customer names/addresses for export
function enrichStops(stops, customers) {
  const byId = new Map(customers.map(c => [c.id, c]));
  return stops.map(s => {
    const c = byId.get(s.custId);
    return { ...s, customerName: c?.name || '', customerAddr: c?.addr || '' };
  });
}
function enrichInvoices(invoices, customers) {
  const byId = new Map(customers.map(c => [c.id, c]));
  return invoices.map(i => ({ ...i, customerName: byId.get(i.custId)?.name || '' }));
}

// ─── Public CSV generators ───────────────────────────────────
export function customersToCsv(customers) {
  return toCsv(customers, CUSTOMER_FIELDS);
}
export function invoicesToCsv(invoices, customers) {
  return toCsv(enrichInvoices(invoices, customers), INVOICE_FIELDS);
}
export function stopsToCsv(stops, customers) {
  return toCsv(enrichStops(stops, customers), STOP_FIELDS);
}

// ─── PDF (table layout) ──────────────────────────────────────
async function generatePdf(title, rows, headers, opts = {}) {
  const jsPDF = await loadJsPDF();
  const orientation = headers.length > 8 ? 'landscape' : 'portrait';
  const doc = new jsPDF({ orientation, unit: 'pt', format: 'letter' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 36;

  // Header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('PainterOps', margin, margin + 4);
  doc.setFontSize(13);
  doc.text(title, margin, margin + 22);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(`Generated ${new Date().toLocaleString('en-US')}  ·  ${rows.length} row${rows.length === 1 ? '' : 's'}`, margin, margin + 36);
  doc.setTextColor(0);

  // Table
  const tableTop = margin + 56;
  const usableW  = pageW - margin * 2;
  const colW     = usableW / headers.length;
  const rowH     = 18;
  let y = tableTop;

  // Header row
  doc.setFillColor(240);
  doc.rect(margin, y, usableW, rowH, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  headers.forEach((h, i) => {
    doc.text(String(h.label).toUpperCase(), margin + i * colW + 4, y + 12);
  });
  y += rowH;
  doc.setFont('helvetica', 'normal');

  // Data rows
  const truncate = (s, max) => {
    s = String(s);
    if (s.length <= max) return s;
    return s.slice(0, max - 1) + '…';
  };

  for (const row of rows) {
    if (y + rowH > pageH - margin) {
      doc.addPage();
      y = margin;
    }
    headers.forEach((h, i) => {
      const raw = typeof h.value === 'function' ? h.value(row) : row[h.value];
      const txt = truncate(raw ?? '', Math.floor(colW / 5));
      doc.text(String(txt), margin + i * colW + 4, y + 12);
    });
    if (rows.indexOf(row) % 2 === 1) {
      // light stripe
      doc.setFillColor(248);
      doc.rect(margin, y, usableW, rowH, 'F');
      // overwrite with text again because fill covered it
      headers.forEach((h, i) => {
        const raw = typeof h.value === 'function' ? h.value(row) : row[h.value];
        const txt = truncate(raw ?? '', Math.floor(colW / 5));
        doc.text(String(txt), margin + i * colW + 4, y + 12);
      });
    }
    y += rowH;
  }

  // Footer page numbers
  const pageCount = doc.internal.getNumberOfPages();
  doc.setFontSize(8);
  doc.setTextColor(140);
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.text(`Page ${p} of ${pageCount}`, pageW - margin, pageH - 18, { align: 'right' });
  }

  return doc;
}

export async function customersToPdfDataUri(customers) {
  const doc = await generatePdf('Customers Export', customers, CUSTOMER_FIELDS);
  return doc.output('datauristring');
}
export async function invoicesToPdfDataUri(invoices, customers) {
  const doc = await generatePdf('Invoices Export', enrichInvoices(invoices, customers), INVOICE_FIELDS);
  return doc.output('datauristring');
}
export async function stopsToPdfDataUri(stops, customers) {
  const doc = await generatePdf('Routes / Stops Export', enrichStops(stops, customers), STOP_FIELDS);
  return doc.output('datauristring');
}

export async function customersToPdf(customers) {
  return generatePdf('Customers Export', customers, CUSTOMER_FIELDS);
}
export async function invoicesToPdf(invoices, customers) {
  return generatePdf('Invoices Export', enrichInvoices(invoices, customers), INVOICE_FIELDS);
}
export async function stopsToPdf(stops, customers) {
  return generatePdf('Routes / Stops Export', enrichStops(stops, customers), STOP_FIELDS);
}

// ─── Download triggers ───────────────────────────────────────
export function downloadCustomersCsv(customers) {
  downloadFile(`customers-${todayStamp()}.csv`, customersToCsv(customers), 'text/csv');
}
export function downloadInvoicesCsv(invoices, customers) {
  downloadFile(`invoices-${todayStamp()}.csv`, invoicesToCsv(invoices, customers), 'text/csv');
}
export function downloadStopsCsv(stops, customers) {
  downloadFile(`stops-${todayStamp()}.csv`, stopsToCsv(stops, customers), 'text/csv');
}
export async function downloadCustomersPdf(customers) {
  const doc = await customersToPdf(customers);
  doc.save(`customers-${todayStamp()}.pdf`);
}
export async function downloadInvoicesPdf(invoices, customers) {
  const doc = await invoicesToPdf(invoices, customers);
  doc.save(`invoices-${todayStamp()}.pdf`);
}
export async function downloadStopsPdf(stops, customers) {
  const doc = await stopsToPdf(stops, customers);
  doc.save(`stops-${todayStamp()}.pdf`);
}

function todayStamp() {
  return new Date().toISOString().slice(0, 10);
}

// Get PDF as base64 (without data: prefix) for email attachments
export function pdfToBase64(doc) {
  const dataUri = doc.output('datauristring');
  return dataUri.split(',')[1] || '';
}
